import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Slide, Snackbar, useTheme } from '@mui/material';
import { useStore } from 'zustand';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import { getContrastTextColor } from '../../utils/getCintrastTextColor';
import { TransitionProps } from '@mui/material/transitions';
import {
  calculateRealWorldDistance,
  calculatePolygonArea,
  calculateRealWorldArea,
} from '../svgElements/takeoffHelper';
import { useUnityBuildStore } from '../../stores/store';

// Custom slide transition from left
const SlideTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return (
    <Slide
      direction="right"
      ref={ref}
      {...props}
      timeout={{
        enter: 500,
        exit: 300,
      }}
    />
  );
});

export default function TakeoffNotification() {
  const theme = useTheme();
  const currentTakeoffAssembly = useStore(useAssemblyStore).currentTakeoffAssembly;
  const isViewingAssemblyForm = useStore(useAssemblyStore).isViewingAssemblyForm;
  const userScale = useStore(useUnityBuildStore).userScale;
  const assemblyOption = useStore(useAssemblyStore).assemblyOption;
  const [hasAnimated, setHasAnimated] = useState(false);

  // Calculate real-time measurements from current points
  const realTimeMeasurements = useMemo(() => {
    if (!currentTakeoffAssembly?.pointsList || currentTakeoffAssembly.pointsList.length === 0) {
      return { length: 0, area: 0 };
    }

    const points = currentTakeoffAssembly.pointsList;

    // Calculate total segment length only for segments where destination point allows trailing line
    let totalLength = 0;
    for (let i = 0; i < points.length - 1; i++) {
      const destinationPoint = points[i + 1];
      // Only include segment if the destination point allows trailing line
      if (destinationPoint.allowtrailingline) {
        const dx = destinationPoint.x - points[i].x;
        const dy = destinationPoint.y - points[i].y;
        const segmentLength = Math.sqrt(dx * dx + dy * dy);
        totalLength += segmentLength;
      }
    }

    // Calculate area only if assembly type is area and we have enough points
    let area = 0;
    if (currentTakeoffAssembly?.assemblymeasurementtype === 'area' && points.length >= 3) {
      area = calculatePolygonArea(points);
    }

    return {
      length: totalLength,
      area: area,
    };
  }, [currentTakeoffAssembly?.pointsList, currentTakeoffAssembly?.assemblymeasurementtype]);

  // Show notification when assembly changes
  useEffect(() => {
    if (currentTakeoffAssembly) {
      setHasAnimated(false);
      // Set hasAnimated after a brief delay to trigger the animation
      const timer = setTimeout(() => {
        setHasAnimated(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentTakeoffAssembly]);

  // Don't show notification if CreateAssembly is open
  if (!currentTakeoffAssembly || (isViewingAssemblyForm && assemblyOption === 'takeoff'))
    return null;

  return (
    <Snackbar
      open={true}
      TransitionComponent={SlideTransition}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      sx={{
        position: 'absolute',
        top: '80px !important',
        left: '16px !important',
        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: hasAnimated ? 1 : 0,
        transform: hasAnimated ? 'none' : 'translateX(-100%)',
        '& .MuiSnackbarContent-root': {
          backgroundColor: 'transparent',
          padding: 0,
          minWidth: 'unset',
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          color: getContrastTextColor(theme.palette.background.paper),
          padding: '16px',
          borderRadius: '8px',
          border: 1,
          borderColor: 'divider',
          boxShadow: theme.shadows[2],
          minWidth: '280px',
          maxWidth: '320px',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          transform: hasAnimated ? 'none' : 'translateX(-20px)',
          transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: theme.shadows[4],
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            opacity: 0.7,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontSize: '0.75rem',
            color: theme.palette.text.secondary,
          }}
        >
          Current Takeoff
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: '1rem',
            lineHeight: 1.4,
          }}
        >
          {currentTakeoffAssembly?.assemblyname || 'Unknown Assembly'}
        </Typography>

        {/* Measurements Container */}
        {(currentTakeoffAssembly?.pointsList?.length > 0 ||
          realTimeMeasurements.length > 0 ||
          realTimeMeasurements.area > 0 ||
          currentTakeoffAssembly?.itemsList?.[0]?.breakout) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              mt: 1,
              pt: 1,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            {/* Breakout */}
            {currentTakeoffAssembly.itemsList?.[0]?.breakout && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  pb: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.text.secondary,
                    fontSize: '0.875rem',
                  }}
                >
                  Breakout:
                </Typography>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.warning.main,
                    opacity: 0.8,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.text.secondary,
                    fontSize: '0.875rem',
                  }}
                >
                  {currentTakeoffAssembly.itemsList?.[0]?.breakout?.breakoutname}
                </Typography>
              </Box>
            )}

            {/* Points - show for all assembly types */}
            {currentTakeoffAssembly?.pointsList?.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                    opacity: 0.8,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.text.secondary,
                    fontSize: '0.875rem',
                  }}
                >
                  {currentTakeoffAssembly?.pointsList?.filter(point => point.isincludedincount)
                    .length || 0}{' '}
                  {(currentTakeoffAssembly?.pointcount || 0) > 1 ? 'points' : 'point'}
                </Typography>
              </Box>
            )}

            {/* Segment Length - only show for length assemblies */}
            {currentTakeoffAssembly?.assemblymeasurementtype === 'length' &&
              realTimeMeasurements.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.secondary.main,
                      opacity: 0.8,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                    }}
                  >
                    {calculateRealWorldDistance(realTimeMeasurements.length, userScale).toFixed(2)}{' '}
                    ft
                  </Typography>
                </Box>
              )}

            {/* Area - only show for area type assemblies */}
            {currentTakeoffAssembly?.assemblymeasurementtype === 'area' &&
              realTimeMeasurements.area > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.success.main,
                      opacity: 0.8,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                    }}
                  >
                    {calculateRealWorldArea(realTimeMeasurements.area, userScale).toFixed(2)} ft²
                  </Typography>
                </Box>
              )}
          </Box>
        )}
      </Box>
    </Snackbar>
  );
}
